import classNames from 'classnames';
import React, { useContext, useMemo, useState } from 'react';
import type {
  DraggableStateSnapshot,
  DraggingStyle,
  NotDraggingStyle,
} from 'react-beautiful-dnd';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import * as styles from './NewReorderableList.module.scss';
import SVGIcon from '../components/SVGIcon';
import dragIcon from '../icons/non-prefilled/16x16/drag.svg';

type DragState = {
  sourceIndex: number;
  destinationIndex: number;
};

type InnerContextType = {
  isDisabled?: boolean;
  dragState: DragState | null;
};

const InnerContext = React.createContext<InnerContextType>({
  isDisabled: false,
  dragState: null,
});

interface NewReorderableListProps {
  disabled?: boolean;
  children: React.ReactNode;
  name: string;
  onMove?: (sourceIndex: number, destinationIndex: number) => void;
}

const NewReorderableList = ({
  disabled,
  children,
  name,
  onMove,
}: NewReorderableListProps) => {
  const [dragState, setDragState] = useState<DragState | null>(null);
  const value = useMemo(
    () => ({ isDisabled: disabled, dragState }),
    [disabled, dragState],
  );

  return (
    <InnerContext.Provider value={value}>
      <DragDropContext
        onDragEnd={({ destination, source }) => {
          setDragState(null);
          if (onMove && destination && destination.index !== source.index) {
            onMove(source.index, destination.index);
          }
        }}
        onDragUpdate={({ destination, source }) => {
          if (destination) {
            setDragState({
              destinationIndex: destination.index,
              sourceIndex: source.index,
            });
          } else {
            setDragState(null);
          }
        }}
      >
        <Droppable
          isDropDisabled={disabled}
          droppableId={`${name}-row-droppable`}
        >
          {({ droppableProps, innerRef: droppableRef, placeholder }) => {
            return (
              <div
                {...droppableProps}
                ref={droppableRef}
                className={styles.container}
              >
                {children}
                <span style={{ display: 'none' }}>{placeholder}</span>
              </div>
            );
          }}
        </Droppable>
      </DragDropContext>
    </InnerContext.Provider>
  );
};

const DragHandle = (
  props: Omit<React.HTMLAttributes<HTMLDivElement>, 'className'>,
) => (
  <div className={styles.dragHandle} {...props}>
    <SVGIcon className={styles.dragIcon}>{dragIcon}</SVGIcon>
  </div>
);

const getDraggingStyle = (
  style: DraggingStyle | NotDraggingStyle | undefined,
  snapshot: DraggableStateSnapshot,
) => {
  if (!snapshot.isDragging) {
    return undefined;
  }

  if (!snapshot.isDropAnimating) {
    return {
      ...style,
      left: 'auto',
    };
  }

  return {
    ...style,
    left: 'auto',
    transitionDuration: `0.001s`,
  };
};

interface RowProps {
  children: (
    snapshot: DraggableStateSnapshot,
    dragState: DragState | null,
  ) => React.ReactNode;
  id: string;
  index: number;
  className?: string;
}

const Row = ({ children, id, index, className }: RowProps) => {
  const { isDisabled, dragState } = useContext(InnerContext);
  const rowClassName = classNames(styles.rowContainer, className);

  return (
    <Draggable isDragDisabled={isDisabled} draggableId={id} index={index}>
      {(
        { dragHandleProps, innerRef: draggableRef, draggableProps },
        snapshot,
      ) => {
        const content = (
          <div className={styles.content}>{children(snapshot, dragState)}</div>
        );
        return (
          <>
            <div
              className={classNames(rowClassName, {
                [styles.isDragging]: snapshot.isDragging,
                [styles.hasActiveDrag]: dragState != null,
              })}
              ref={draggableRef}
              {...draggableProps}
              style={getDraggingStyle(draggableProps.style, snapshot)}
            >
              {!isDisabled && <DragHandle {...dragHandleProps} />}
              {content}
            </div>
            {snapshot.isDragging && (
              <div className={classNames(rowClassName, styles.placeholder)}>
                <DragHandle {...dragHandleProps} />
                {content}
              </div>
            )}
          </>
        );
      }}
    </Draggable>
  );
};

NewReorderableList.Row = Row;

interface DragIndicatorProps {
  children: React.ReactNode;
  className?: string;
  index: number;
}

const DragIndicator = ({ className, children, index }: DragIndicatorProps) => {
  const { dragState } = useContext(InnerContext);
  return (
    <span
      className={classNames(styles.dragIndicator, className, {
        [styles.topActive]:
          dragState?.destinationIndex === index &&
          dragState.sourceIndex > dragState.destinationIndex,
        [styles.bottomActive]:
          dragState?.destinationIndex === index &&
          dragState.sourceIndex < dragState.destinationIndex,
      })}
    >
      <span className={styles.dragIndicatorTop} />
      {children}
      <span className={styles.dragIndicatorBottom} />
    </span>
  );
};

NewReorderableList.DragIndicator = DragIndicator;

export default NewReorderableList;
